import { ComponentRef, Directive, ElementRef, Input, OnDestroy, ViewContainerRef } from '@angular/core';
import { first, Observable, Subject } from 'rxjs';
import { InputLoadingComponent } from '../../components/input-loading/input-loading.component';

/**
 * Adds a loading spinner to the input. Can be used with an observable or direct boolean value
 * @example
 *  [rxInputLoading]="of([123])"
 *  [rxInputLoading]="true"
 */
@Directive({
  selector: '[rxInputLoading]',
})
export class InputLoadingDirective implements OnDestroy {
  private readonly inputWrapperClass = '.input-wrapper';
  destroyed$: Subject<void> = new Subject<void>();
  loadingComponent: ComponentRef<InputLoadingComponent>;

  @Input() set rxInputLoading(isLoading: Observable<unknown> | boolean) {
    if (isLoading instanceof Observable) {
      this.loadingByObservable(isLoading);
    } else {
      isLoading ? this.show() : this.hide();
    }
  }

  constructor(private viewContainerRef: ViewContainerRef, private element: ElementRef) {}

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private loadingByObservable(isLoading: Observable<unknown>): void {
    this.show();
    isLoading.pipe(first()).subscribe({
      next: () => {
        this.hide();
      },
      error: () => {
        this.hide();
      },
      complete: () => {
        this.hide();
      },
    });
  }

  show(): void {
    if (!this.loadingComponent) {
      this.loadingComponent = this.viewContainerRef.createComponent(InputLoadingComponent);
      this.element.nativeElement
        .querySelector(this.inputWrapperClass)
        .appendChild(this.loadingComponent.location.nativeElement);
    }
  }

  hide(): void {
    const host: HTMLElement = this.element.nativeElement;
    if (this.loadingComponent && host.querySelector('rx-applications-input-loading')) {
      host.querySelector(this.inputWrapperClass)?.removeChild(this.loadingComponent.location.nativeElement);
      this.loadingComponent.destroy();
      this.loadingComponent = null;
    }
  }
}
